"use client";

import { forwardRef } from "react";
import type { CSSProperties } from "react";
import { SectionBlock } from "@/components/blocks/SectionBlock";
import { AccentStar } from "@/shared/ui/accent-star";
import { useScrollReveal, type RevealState } from "@/shared/lib/useScrollReveal";
import { useMemo } from "react";
import type { SelectionStep } from "@/entities/selection-step";

/* ─────────────────────────────────────────────────────────────
   Тайминги: строго последовательно
   Цифра + текст → разделитель + подзаголовок + заголовок (issue 1)
   Звёздочки → одновременно с заголовком шага (issue 2)
───────────────────────────────────────────────────────────── */
const HEADER_DUR   = 0.65;
const DESC_OFFSET  = 0.3;
const DESC_STEP    = 0.15;
const DESC_DUR     = 0.55;
const STAR_OFFSET  = 0.14;
const STAR_DUR     = 0.45;
const STEP_GAP     = 0.1;

interface StepTiming {
  headerDelay: number;
  descDelays: number[];
  starDelays: number[];
}

function buildTimings(steps: SelectionStep[]): StepTiming[] {
  const result: StepTiming[] = [];
  let cursor = 0.1;

  for (let si = 0; si < steps.length; si++) {
    const numDescs = steps[si].description.length;
    const numStars = si + 1;
    const headerDelay = cursor;

    const descDelays: number[] = Array.from(
      { length: numDescs },
      (_, di) => cursor + DESC_OFFSET + di * DESC_STEP,
    );

    // Звёздочки стартуют одновременно с заголовком (issue 2):
    // delay = headerStart + headerDuration → сразу как заголовок появился
    const starDelays: number[] = Array.from(
      { length: numStars },
      (_, k) => cursor + HEADER_DUR + 0.05 + k * STAR_OFFSET,
    );

    // Следующий шаг стартует после окончания последнего desc-пункта
    const lastDescEnd = descDelays[numDescs - 1] + DESC_DUR;
    cursor = lastDescEnd + STEP_GAP;

    result.push({ headerDelay, descDelays, starDelays });
  }

  return result;
}

/* ─────────────────────────────────────────────────────────────
   Стиль-хелперы
───────────────────────────────────────────────────────────── */

/** Заголовочный блок секции: одним куском сверху вниз (issue 1) */
function headingBlock(state: RevealState): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "translateY(-2.5rem)" };
  return {
    opacity: 0,
    animation: `slide-from-top ${HEADER_DUR}s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0s forwards`,
  };
}

/** Заголовок шага: одним куском сверху вниз */
function stepHeading(state: RevealState, delay: number): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "translateY(-2.5rem)" };
  return {
    opacity: 0,
    animation: `slide-from-top ${HEADER_DUR}s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay.toFixed(2)}s forwards`,
  };
}

function fromRight(state: RevealState, delay: number): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "translateX(120%)" };
  return {
    opacity: 0,
    animation: `slide-from-right-far ${DESC_DUR}s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay.toFixed(2)}s forwards`,
  };
}

function starBump(state: RevealState, delay: number): CSSProperties {
  if (state !== "animating") return {};
  return {
    animation: `star-bump ${STAR_DUR}s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay.toFixed(2)}s 1`,
  };
}

/* ─────────────────────────────────────────────────────────────
   Компонент
───────────────────────────────────────────────────────────── */
interface SelectionProcessSectionProps {
  steps: SelectionStep[];
}

export const SelectionProcessSection = forwardRef<HTMLElement, SelectionProcessSectionProps>(({ steps }, ref) => {
  const { ref: revealRef, state } = useScrollReveal("selection", 0.15);
  const TIMINGS = useMemo(() => buildTimings(steps), [steps]);

  const setRefs = (el: HTMLElement | null) => {
    revealRef(el);
    if (typeof ref === "function") ref(el);
    else if (ref)
      (ref as React.MutableRefObject<HTMLElement | null>).current = el;
  };

  return (
    <section
      ref={setRefs}
      id="selection-process"
      className="flex flex-col min-h-[clamp(400px,80svh,1100px)] overflow-hidden"
    >
      <SectionBlock className="flex flex-col gap-fluid-2xl p-fluid-xl h-full">

        {/* ── Заголовок блока: одним блоком сверху вниз ── */}
        <div className="overflow-hidden">
          <div style={headingBlock(state)} className="text-left">
            <h2 className="font-content font-semibold text-fluid-4xl leading-tight text-foreground">
              Специалисты, которым <span className="text-help">можно доверять</span>
            </h2>
            <p className="font-content text-fluid-lg leading-relaxed text-foreground/80 mt-fluid-sm max-w-[65ch]">
              Чтобы стать частью Practicum Help, специалист проходит отбор
            </p>
          </div>
        </div>

        {/* ── Шаги: строго последовательно ── */}
        <div className="flex flex-col gap-fluid-lg pt-fluid-lg border-t border-[var(--help)]/20">
          {steps.map((item, si) => {
            const t = TIMINGS[si];
            return (
              <div key={item.step} className="relative">
                <div className="flex flex-col gap-fluid-md py-fluid-md">

                  {/* Заголовок шага: звёзды + h3 + summary — из верхней границы */}
                  <div
                    style={stepHeading(state, t.headerDelay)}
                    className="flex flex-col gap-fluid-xs"
                  >
                    {/* Звёздочки: боунс одновременно с заголовком (issue 2) */}
                    <div className="flex items-center gap-1.5">
                      {t.starDelays.map((starDelay, k) => (
                        <AccentStar
                          key={k}
                          color="var(--help)"
                          size={26}
                          style={starBump(state, starDelay)}
                        />
                      ))}
                    </div>
                    <h3 className="font-heading text-fluid-xl text-foreground leading-snug">
                      {item.title}
                    </h3>
                    <p className="hidden font-content text-fluid-sm text-foreground/55 leading-relaxed">
                      {item.summary}
                    </p>
                  </div>

                  {/* section overflow-hidden клипирует влёт справа */}
                  <ul className="flex flex-col gap-2">
                    {item.description.map((text, di) => (
                      <li
                        key={di}
                        style={{
                          background:
                            "linear-gradient(90deg, rgba(6,155,147,0.18) 0%, rgba(6,155,147,0.04) 60%, transparent 100%)",
                          ...fromRight(state, t.descDelays[di]),
                        }}
                        className="px-fluid-md py-fluid-sm rounded-lg"
                      >
                        <span className="font-content text-fluid-base text-foreground/90 leading-snug">
                          {text}
                        </span>
                      </li>
                    ))}
                  </ul>

                </div>
              </div>
            );
          })}
        </div>

      </SectionBlock>
    </section>
  );
});

SelectionProcessSection.displayName = "SelectionProcessSection";
